/**
 * Sprint 15 Ola 7 — AuriaCommandRegistry
 * Pure JS module. No React. No side effects.
 * Exports AURIA_COMMANDS map + detectCommand(text) function.
 *
 * NOTE on ordering: detectCommand iterates AURIA_COMMANDS in insertion order.
 * More specific commands (UNREALISTIC_GOAL) must appear before general ones
 * (ADJUST_VOLUME) to avoid false positives on overlapping terms like "demasiado".
 *
 * NOTE on accents: Spanish accents (ó, é, í, á, ú) are handled with Unicode
 * character classes in regexes. The /u flag enables full Unicode support.
 */

const AURIA_COMMANDS = {
  // MACRO MAESTRO ARIA: Nivel 3 ejecutivo (tipado · con confirmación)
  SKIP_TODAY: {
    id: 'SKIP_TODAY',
    label: 'Saltar entreno de hoy',
    trigger: /hoy (no entreno|no puedo entrenar|mejor descanso|salto el entreno)/iu,
    prompt_template: 'El usuario quiere saltarse el entrenamiento de hoy. Confirma antes de ejecutar con el bloque <aria-action type="SKIP_TODAY">.',
    executive: true,
  },
  TRAVEL_TRIP: {
    id: 'TRAVEL_TRIP',
    label: 'Comunicar viaje',
    trigger: /me voy|viajo|viaje|salgo de viaje|estoy de viaje|estar[ée] fuera|fuera de casa|finde fuera|semana fuera/iu,
    // BLOQUE compás·P3 (L11) · parseo ACOTADO a ventana de fechas + equipamiento (NO campo libre).
    // Extrae start/end (ISO, resueltos contra el HOY del usuario · "la semana que viene", "del 12 al 16",
    // "4 días") y equipment (gym|none|unknown desde "hotel con gym"/"sin nada"/"gimnasio completo").
    // AMBIGÜEDAD → repregunta UNA vez ("¿del 12 al 16 incluido?"), jamás asumas una fecha.
    prompt_template: 'El usuario comunica un viaje/ausencia futura. Extrae la VENTANA de fechas (start y end en ISO YYYY-MM-DD, resueltos contra la fecha de hoy del usuario) y el equipamiento (gym | none | unknown). Si la ventana es ambigua, PREGUNTA una vez antes de confirmar. Luego confirma con <aria-action type="TRAVEL_TRIP" params=\'{"start":"YYYY-MM-DD","end":"YYYY-MM-DD","equipment":"gym|none|unknown"}\'>.',
    executive: true,
  },
  // T4·FRENTE 2 · CHIPS DE CAOS · deben ir ANTES de los genéricos (ADJUST_VOLUME/MOVE_ITEM) para
  // ganar la detección. Cada uno mapea a un intent ejecutivo con consumidor serve-time real.
  NO_GYM_TODAY: {
    id: 'NO_GYM_TODAY',
    label: 'Hoy no llego al gym',
    trigger: /no (llego|puedo ir|voy) al (gym|gimnasio)|sin (gym|gimnasio) hoy|hoy sin (gym|gimnasio)|no tengo gym/iu,
    prompt_template: 'El usuario no tiene gym hoy. Confirma pasar la sesión a bandas + bodyweight en casa con <aria-action type="NO_GYM_TODAY">.',
    executive: true,
  },
  RUNNING_LATE: {
    id: 'RUNNING_LATE',
    label: 'Llego tarde / poco tiempo',
    trigger: /llego tarde|voy tarde|poco tiempo|no me da tiempo|se me ha hecho tarde|solo tengo \d+ *min|versi[oó]n corta|expr[eé]s/iu,
    prompt_template: 'El usuario tiene poco tiempo hoy. Confirma una versión exprés que respeta lo esencial con <aria-action type="RUNNING_LATE">.',
    executive: true,
  },
  LOW_ENERGY: {
    id: 'LOW_ENERGY',
    label: 'Sin ganas / sin energía',
    trigger: /sin ganas|sin energ[ií]a|no tengo ganas|hoy no puedo m[aá]s|estoy reventado|agotad[oa]|sin fuerzas|me cuesta hoy/iu,
    prompt_template: 'El usuario está bajo de energía. Confirma bajar la carga de hoy sin culpa con <aria-action type="LOW_ENERGY">.',
    executive: true,
  },
  PLAN_CHANGE: {
    id: 'PLAN_CHANGE',
    label: 'Cambio de plan',
    trigger: /cambio de plan|cambi[oó] mi plan|me surgi[oó] algo|imprevisto|ha surgido algo|necesito cambiar (hoy|el d[ií]a)/iu,
    prompt_template: 'El usuario tiene un imprevisto. Ofrece las salidas concretas (saltar, más suave, exprés, sin gym, viaje) con <aria-action type="PLAN_CHANGE">.',
    executive: true,
  },
  SWAP_MEAL: {
    id: 'SWAP_MEAL',
    label: 'Cambiar comida',
    trigger: /cambia.*(comida|plato|cena|almuerzo|desayuno)|no (tengo|me gusta|como)|sustituy/iu,
    prompt_template: 'El usuario quiere cambiar una comida. Pregunta qué comida y el motivo. Confirma con <aria-action type="SWAP_MEAL">.',
    executive: true,
  },
  // Nivel 2: consultivo
  MOVE_ITEM: {
    id: 'MOVE_ITEM',
    label: 'Mover actividad',
    trigger: /mueve?|traslada?|cambia.*(d[íi]a|fecha)/iu,
    prompt_template: 'El usuario quiere mover una actividad. Pregunta: ¿qué actividad y a qué día?',
  },
  REPORT_INJURY: {
    id: 'REPORT_INJURY',
    label: 'Reportar lesión',
    trigger: /lesi[oó]n|lesionado|dolor|me duele|molestia/iu,
    prompt_template: 'El usuario reporta una lesión o dolor. Activa el flujo de triage de lesión.',
  },
  REPORT_EVENT: {
    id: 'REPORT_EVENT',
    label: 'Reportar evento',
    trigger: /viaj|fiesta|boda|evento|compromiso|no puedo/iu,
    prompt_template: 'El usuario reporta un evento que afecta al plan. Pregunta qué días y cómo adaptar.',
  },
  EXPLAIN: {
    id: 'EXPLAIN',
    label: 'Explicar decisión',
    trigger: /por qu[eé]|expl[ií]c[aá]|raz[oó]n|motivo|entiendo/iu,
    prompt_template: 'El usuario pide explicación de una decisión del motor. Usa el contexto disponible.',
  },
  UNREALISTIC_GOAL: {
    id: 'UNREALISTIC_GOAL',
    label: 'Objetivo poco realista',
    // Must be before ADJUST_VOLUME to match "demasiado ambicioso" before "demasiado"
    trigger: /objetivo|meta\b|realista|imposible|demasiado ambicioso/iu,
    prompt_template: 'El usuario cuestiona la viabilidad de su objetivo. Analiza con datos reales.',
  },
  ADJUST_VOLUME: {
    id: 'ADJUST_VOLUME',
    label: 'Ajustar volumen',
    trigger: /volumen|carga|intensidad|demasiado|muy (poco|mucho)/iu,
    prompt_template: 'El usuario quiere ajustar el volumen de entrenamiento.',
  },
  SUBSTITUTE_MEAL: {
    id: 'SUBSTITUTE_MEAL',
    label: 'Sustituir comida',
    trigger: /sustituy|cambia.*(comida|plato|cena|almuerzo|desayuno)|no (tengo|me gusta)/iu,
    prompt_template: 'El usuario quiere sustituir una comida. Sugiere alternativas con macros equivalentes.',
  },
  WEEKLY_REVIEW: {
    id: 'WEEKLY_REVIEW',
    label: 'Revisión semanal',
    trigger: /semana|resumen|c[oó]mo (voy|estoy|ha ido)/iu,
    prompt_template: 'El usuario pide un resumen de su semana. Usa el contexto de progreso disponible.',
  },
}

/**
 * Detect a command from free text. Returns the matching command object or null.
 * @param {string} text
 * @returns {object|null}
 */
function detectCommand(text) {
  if (!text || typeof text !== 'string') return null
  for (const cmd of Object.values(AURIA_COMMANDS)) {
    if (cmd.trigger.test(text)) return cmd
  }
  return null
}

module.exports = { AURIA_COMMANDS, detectCommand }
